<template>
  <ResponsiveLayout :progress="progress">
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="handleNavigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper">
      <InsuranceVerificationHUKSection
        v-if="hukVerificationToken"
        :corporate="corporate.key"
        :token="hukVerificationToken"
        :type="hukVerificationType"
        :user="user"
        @retry="handleVerificationRetry"
      />
      <InsuranceExternalRedirect
        v-else
        ref="insuranceExternalRedirectRef"
        :cta="
          $t('eu__insurance_verification__private__huk_external_cta_v9.1.0')
        "
        :description="fromAppRedirectDescription"
        @click="handleExternalRedirectClickEvent"
      />
    </div>
    <BaseModal
      ref="thirdPartyLinkConfirmationRef"
      :title="$t('generic__third_party_url_confirmation__header')"
    >
      <div class="kds-content-block">
        <BaseButton
          size="small"
          :text="$t('generic__third_party_url_confirmation__primary_label')"
          @click="handleThirdPartyConfirmationSubmit"
        />
        <BaseButton
          size="small"
          :text="$t('generic__third_party_url_confirmation__secondary_label')"
          variant="outlined"
          @click="handleThirdPartyConfirmationCancel"
        />
      </div>
    </BaseModal>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useUserStore from '@shared/stores/useUserStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useSharedStore from '@shared/stores/useSharedStore.js'
import { useI18n } from 'petite-vue-i18n'
import InsuranceVerificationHUKSection from '@eu/app/components/InsuranceVerificationHUKSection.vue'
import InsuranceExternalRedirect from '@eu/app/components/InsuranceExternalRedirect.vue'
import Tracker from '@shared/Tracker.js'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import assembleKaiaAppDeepLink from '@shared/helpers/assembleKaiaAppDeepLink.js'
import openExternalUrl from '@shared/helpers/openExternalUrl.js'
import ButtonBack from '@shared/components/ButtonBack.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import BaseModal from '@shared/components/BaseModal.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'

const { t } = useI18n()
const userStore = useUserStore()
const corporateStore = useCorporateStore()
const sharedStore = useSharedStore()
const route = useRoute()
const router = useRouter()
const { navigateBack, canGoBack } = useNavigation(
  ROUTE.INSURANCE_VERIFICATION_HUK,
)
const { progress } = useOnboardingProgress()

const corporateExternalUrl = ref('')
const thirdPartyLinkConfirmationRef = ref(null)
const insuranceExternalRedirectRef = ref(null)

const user = computed(() => userStore.user)
const corporate = computed(() => corporateStore.corporate)
const initialQuery = computed(() => sharedStore.initialQuery || {})
const hukVerificationType = computed(() => initialQuery.value.type || 'init')
const hukVerificationToken = computed(() => initialQuery.value.token || null)

const fromAppRedirectDescription = computed(() =>
  t('eu__insurance_verification__private__description__group6_v9.1.0'),
)

onMounted(async () => {
  corporateExternalUrl.value = corporate.value.signupInfoPageUrl || ''
  if (isFromApp()) {
    corporateExternalUrl.value = await assembleKaiaAppDeepLink({
      path: 'goto/app',
      query: { target: 'huk' },
      addOneTimeAuthToken: true,
      fallback: () =>
        assembleKaiaAppDeepLink({
          path: 'goto/app',
          query: { target: 'huk', token: 'invalid' },
        }),
    })
    if (!hukVerificationToken.value) {
      openExternalUrl(corporateExternalUrl.value)
    }
  }
  Tracker.trackViewScreen('signup_insurance_code_input')
})

const withoutToken = ({ token: _token, ...query }) => query

const handleNavigateBack = () => {
  // Drop the HUK token — the router hook bounces any token-carrying
  // navigation straight back to this verification screen.
  navigateBack({ query: withoutToken(initialQuery.value) })
}

const handleVerificationRetry = () => {
  // Clear both sources: initialQuery is merged across navigations, while a
  // token left in the URL would restart validation after a reload.
  const query = withoutToken(route.query)
  sharedStore.initialQuery = withoutToken(initialQuery.value)
  Tracker.trackButtonSelect('try_again', 'signup_insurance_code_input', {
    private_flow: true,
  })
  return router.replace({ name: ROUTE.INSURANCE_VERIFICATION_HUK, query })
}

const handleExternalRedirectClickEvent = () => {
  if (isFromApp()) {
    Tracker.trackButtonSelect('to_insurance', 'signup_insurance_code_input', {
      private_flow: true,
    })
    insuranceExternalRedirectRef.value.openUrl(corporateExternalUrl.value)
    return
  }
  thirdPartyLinkConfirmationRef.value.open()
}

const handleThirdPartyConfirmationSubmit = () => {
  thirdPartyLinkConfirmationRef.value.close()
  Tracker.trackButtonSelect('to_insurance', 'signup_insurance_code_input', {
    private_flow: true,
  })
  insuranceExternalRedirectRef.value.openUrl(corporateExternalUrl.value)
}

const handleThirdPartyConfirmationCancel = () => {
  thirdPartyLinkConfirmationRef.value.close()
}
</script>
